"use client";

import { c as _c } from "react/compiler-runtime";
import * as Sentry from "@sentry/nextjs";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { BackgroundProvider } from "@/components/providers/background-provider";
import { FCMInitializer } from "@/components/providers/fcm-initializer";
import { OfflineSyncProvider } from "@/components/providers/offline-sync-provider";
import { PullToRefresh } from "@/components/providers/pull-to-refresh";
import { RotateDeviceOverlay } from "@/components/providers/rotate-device-overlay";
import SearchPalette from "@/components/providers/search-palette";
import { ServiceWorkerRegister } from "@/components/providers/service-worker-register";
import { ThemeColorSync } from "@/components/providers/theme-color-sync";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PwaInstallProvider } from "@/hooks/use-pwa-install";
import { authClient } from "@/lib/auth-client";
import { patchToastHaptics } from "@/lib/haptics-toast";
import { createQueryClient, REACT_QUERY_CACHE_MAX_AGE_MS } from "@/lib/react-query";
import { createReactQueryPersister } from "@/lib/shared/offline";
if (typeof window !== "undefined") {
  patchToastHaptics();
}
function isStandaloneDisplayMode() {
  if (typeof window === "undefined") return false;
  const displayModeQuery = window.matchMedia?.("(display-mode: standalone)");
  const iosStandalone = (window.navigator as Navigator & {
    standalone?: boolean;
  }).standalone === true;
  return Boolean(displayModeQuery?.matches) || iosStandalone;
}
function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return Boolean(window.matchMedia?.("(max-width: 768px) and (pointer: coarse)").matches);
}
export default function Providers(t0) {
  const $ = _c(31);
  const {
    children
  } = t0;
  const {
    data: session
  } = authClient.useSession();
  const persistenceScope = session?.user?.id ?? null;
  const previousPersistenceScopeRef = useRef(undefined);
  const [queryClient] = useState(_temp);
  let t1;
  if ($[0] !== persistenceScope) {
    t1 = createReactQueryPersister(persistenceScope);
    $[0] = persistenceScope;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryPersister = t1;
  let t2;
  let t3;
  if ($[2] !== persistenceScope || $[3] !== queryClient) {
    t2 = () => {
      if (previousPersistenceScopeRef.current === undefined) {
        previousPersistenceScopeRef.current = persistenceScope;
        return;
      }
      if (previousPersistenceScopeRef.current !== persistenceScope) {
        queryClient.clear();
        previousPersistenceScopeRef.current = persistenceScope;
      }
    };
    t3 = [persistenceScope, queryClient];
    $[2] = persistenceScope;
    $[3] = queryClient;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== session?.user) {
    t4 = () => {
      const user = session?.user;
      if (user) {
        Sentry.setUser({
          id: user.id,
          phoneNumber: user.phoneNumber
        });
      } else {
        Sentry.setUser(null);
      }
    };
    $[6] = session?.user;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = session?.user;
  let t6;
  if ($[8] !== t5) {
    t6 = [t5];
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t4, t6);
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useEffect(_temp3, t7);
  let t8;
  if ($[11] !== queryPersister) {
    t8 = {
      persister: queryPersister,
      maxAge: REACT_QUERY_CACHE_MAX_AGE_MS
    };
    $[11] = queryPersister;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ThemeColorSync />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ServiceWorkerRegister />;
    t11 = <FCMInitializer />;
    t12 = <SearchPalette />;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
  } else {
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
  }
  let t13;
  if ($[17] !== children) {
    t13 = <PwaInstallProvider><TooltipProvider>{children}</TooltipProvider></PwaInstallProvider>;
    $[17] = children;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  let t15;
  let t16;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <PullToRefresh />;
    t15 = <Toaster position="bottom-center" richColors={true} />;
    t16 = <RotateDeviceOverlay />;
    $[19] = t14;
    $[20] = t15;
    $[21] = t16;
  } else {
    t14 = $[19];
    t15 = $[20];
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t13) {
    t17 = <ThemeProvider attribute="class" enableSystem={true} defaultTheme="system">{t9}<BackgroundProvider>{t10}{t11}{t12}{t13}{t14}{t15}{t16}</BackgroundProvider></ThemeProvider>;
    $[22] = t13;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== queryClient || $[25] !== t17) {
    t18 = <OfflineSyncProvider queryClient={queryClient}>{t17}</OfflineSyncProvider>;
    $[24] = queryClient;
    $[25] = t17;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== queryClient || $[28] !== t18 || $[29] !== t8) {
    t19 = <PersistQueryClientProvider client={queryClient} persistOptions={t8}>{t18}</PersistQueryClientProvider>;
    $[27] = queryClient;
    $[28] = t18;
    $[29] = t8;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  return t19;
}
function _temp3() {
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (orientation: "portrait-primary") => Promise<void>;
  };
  const lockOrientation = orientation?.lock;
  if (!lockOrientation) {
    return;
  }
  const tryLockPortrait = () => {
    if (!isStandaloneDisplayMode() || !isMobileViewport()) {
      return;
    }
    lockOrientation.call(orientation, "portrait-primary").catch(_temp2);
  };
  tryLockPortrait();
  orientation.addEventListener?.("change", tryLockPortrait);
  document.addEventListener("visibilitychange", tryLockPortrait);
  return () => {
    orientation.removeEventListener?.("change", tryLockPortrait);
    document.removeEventListener("visibilitychange", tryLockPortrait);
  };
}
function _temp2() {}
function _temp() {
  return createQueryClient();
}